import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Fingerprint } from 'lucide-react';
import {
  signInWithEmail,
  signInWithGoogle,
  isGoogleAuthAvailable,
  auth,
  resendVerificationEmailWithCredentials,
} from '../../utils/firebase';
import { sanitizeAuthError } from '../../utils/userFacingErrors';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (searchParams.get('verified') === '1') {
      setSuccess('Email verified. Sign in with your password.');
      setNeedsVerification(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate('/dashboard', { replace: true });
      }
    });
    return unsub;
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setNeedsVerification(false);

    try {
      // Используем Firebase Auth
      const token = await signInWithEmail(email.trim(), password);

      // Сохраняем токен для отправки на backend
      localStorage.setItem('firebase_token', token);

      // Backend сам создаст/обновит пользователя в Supabase DB при первом запросе
      // Здесь просто логинимся и переходим
      navigate('/dashboard');
    } catch (err: any) {
      const raw = err?.message || '';
      if (raw === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
        setError(
          'Email not verified yet. Open the latest link in your inbox first — signing in again does not send a new email. Use "Resend verification" only if the link expired.',
        );
        return;
      }
      setError(sanitizeAuthError(raw || 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] font-sans flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md bg-[var(--card)] border border-[var(--border-subtle)] rounded-[var(--radius-xl)] p-6 sm:p-8 relative overflow-hidden shadow-pop">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[var(--brand-subtle)] border border-[var(--border-subtle)] rounded-xl flex items-center justify-center mx-auto mb-4 text-[var(--brand)]">
            <Fingerprint className="w-6 h-6" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-[var(--foreground)] mb-2">Welcome back</h2>
          <p className="text-[var(--text-muted)] text-sm">Sign in to keep creating.</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-2 block pl-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                className="w-full bg-[var(--input)] border border-[var(--border-subtle)] rounded-xl py-3 pl-10 pr-4 text-[var(--foreground)] text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--accent-glow)] outline-none transition-all placeholder:text-[var(--text-disabled)]"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-2 block pl-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[var(--input)] border border-[var(--border-subtle)] rounded-xl py-3 pl-10 pr-4 text-[var(--foreground)] text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--accent-glow)] outline-none transition-all placeholder:text-[var(--text-disabled)]"
                disabled={loading}
                required
                minLength={6}
              />
            </div>
            <p className="mt-2 text-right">
              <Link to="/forgot-password" className="text-xs text-[var(--text-muted)] hover:text-[var(--brand)]">
                Forgot password?
              </Link>
            </p>
          </div>

          {success && (
            <div className="mt-2 rounded border border-emerald-500/20 bg-emerald-500/10 p-2 text-xs text-emerald-300">
              {success}
            </div>
          )}
          {error && (
            <div className="text-red-400 text-xs mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded">
              {error}
            </div>
          )}

          {needsVerification && (
            <button
              type="button"
              disabled={resending || !email || !password}
              onClick={async () => {
                setResending(true);
                setError(null);
                try {
                  await resendVerificationEmailWithCredentials(email.trim(), password);
                  setSuccess(
                    'New verification email sent. Open that link, then sign in here (older links no longer work).',
                  );
                } catch (err) {
                  setError(
                    sanitizeAuthError(
                      err instanceof Error ? err.message : 'Could not resend verification email.',
                    ),
                  );
                } finally {
                  setResending(false);
                }
              }}
              className="w-full rounded-xl border border-amber-500/30 bg-amber-500/10 py-3 text-[10px] font-bold uppercase tracking-widest text-amber-200 hover:border-amber-400 disabled:opacity-50"
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--brand)] text-[var(--brand-foreground)] font-semibold text-sm py-3.5 rounded-xl mt-8 hover:brightness-105 transition-all duration-200 shadow-raised disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 space-y-4">
          {isGoogleAuthAvailable() && (
          <button
            type="button"
            onClick={async () => {
              setLoading(true);
              setError(null);
              try {
                const token = await signInWithGoogle();
                localStorage.setItem('firebase_token', token);

                // Backend сам создаст/обновит пользователя в Supabase DB при первом запросе
                navigate('/dashboard');
              } catch (err: any) {
                setError(sanitizeAuthError(err.message || 'Failed to sign in with Google'));
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-[var(--surface-2)] border border-[var(--border-subtle)] text-[var(--foreground)] font-semibold text-sm py-3.5 rounded-xl hover:bg-[var(--surface-3)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in…' : 'Continue with Google'}
          </button>
          )}

          <div className="text-center border-t border-[var(--border-subtle)] pt-6">
            <p className="text-sm text-[var(--text-muted)]">
              New to Pixlie?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-[var(--foreground)] hover:text-[var(--brand)] font-medium underline underline-offset-4 decoration-[var(--border-strong)] transition-all"
              >
                Create account
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
